import { is, isNil } from 'ramda'
import { CustomAttributes } from 'types/user'
import { validateEmail } from 'utils/email'

import { DriftPublicAPI } from './types'
import { apiError, apiWarning } from './utils'

type ValidatorConfig = Partial<Record<keyof DriftPublicAPI, ValidationParam[]>>

type ValidatorFunction = (method: string, name: string, optional: boolean, value: any) => boolean

type ValidationType = (
  ValidatorFunction |
  StringConstructor |
  NumberConstructor |
  BooleanConstructor |
  FunctionConstructor
)

interface ValidationParam {
  name: string,
  type: ValidationType,
  optional?: boolean
}

export const areAttributesValid = (method: string, name: string, optional: boolean, attributes?: CustomAttributes): boolean => {
  if (isNil(attributes)) {
    return optional
  }

  if (typeof attributes !== 'function' && is(Object, attributes)) {
    const attributeKeys = Object.keys(attributes)
    for (const key of attributeKeys) {
      const value = attributes[key]
      if (!is(Number, value) && !is(String, value) && !is(Boolean, value)) {
        apiError(`${method}(...) "${key}" in ${name} must be number, string, or boolean but instead we got ${typeof value}.`)
        return false
      }
      if (key === 'email' && (!is(String, value) || !validateEmail(value as string))) {
        apiError(`${method}(...) email in ${name} is invalid:`, value)
        return false
      }
    }
    return true
  }

  return false
}

const areStartInteractionParamsValid = (method: string, name: string, optional: boolean, params?: Record<string, any>): boolean => {
  if ((isNil(params) && !optional) || !is(Object, params)) {
    apiError(`${method}({ ... }) must be called with ${name} { interactionId: number, goToConversation: boolean } (goToConversation is optional.)`)
    return false
  }

  if (!params.interactionId || isNil(params.interactionId) || !is(Number, params.interactionId)) {
    apiError(`${method}({ ... }) requires ${name} object with numeric "interactionId" but we got ${typeof params.interactionId}.`)
    return false
  }

  if (params.goToConversation && !is(Boolean, params.goToConversation)) {
    apiError(`${method}({ ... }) "goToConversation" parameter should be boolean but we got ${typeof params.goToConversation}.`)
    return false
  }

  return true
}


const param = (attribute: string, type: ValidationType, optional?: boolean): ValidationParam => ({
  name: attribute,
  type,
  optional
})

const VALIDATOR_CONFIG: ValidatorConfig = {
  config: [
    param('options', Object)
  ],
  collectFormData: [
    param('data', areAttributesValid),
    param('options', Object, true)
  ],
  debug: [
    param('namespace', String, true)
  ],
  goToConversation: [
    param('Conversation ID', Number)
  ],
  goToNewConversation: [
    param('Message', String)
  ],
  identify: [
    param('User ID', String),
    param('attributes', areAttributesValid, true),
    param('options', Object, true)
  ],
  off: [
    param('Event name', String),
    param('handler', Function)
  ],
  on: [
    param('Event name', String),
    param('handler', Function)
  ],
  page: [
    param('Page name', String, true)
  ],
  setUserAttributes: [
    param('attributes', areAttributesValid)
  ],
  setUserJwt: [
    param('User JWT', String)
  ],
  scheduleMeeting: [
    param('Agent ID', Number),
    param('options', Object, true)
  ],
  showAwayMessage: [
    param('Message', String, true)
  ],
  showWelcomeMessage: [
    param('Message', String, true)
  ],
  startInteraction: [
    param('params', areStartInteractionParamsValid)
  ],
  toggleHoursAndTargeting: [
    param('enabled', Boolean)
  ],
  track: [
    param('Event name', String),
    param('attributes', areAttributesValid, true)
  ]
}

const getTypeName = (value: any) => {
  if (value === String || is(String, value)) {
    return 'string'
  }

  if (value === Boolean || is(Boolean, value)) {
    return 'boolean'
  }

  if (value === Number || is(Number, value)) {
    return 'number'
  }

  if (value === Object || is(Object, value)) {
    return 'object'
  }

  if (value === Function || is(Function, value)) {
    return 'function'
  }

  if (isNil(value)) {
    return 'null'
  }

  return 'unknown'
}

const areArgsValid = <T>(method: string, args: T[], validators: ValidationParam[]): boolean => {
  const numArgs = args.length
  const minNumParams = validators.filter(validator => !validator.optional).length

  return validators.reduce<boolean>((acc, value, index) => {
    const { name, type, optional = false } = value

    if (index >= numArgs) {
      if (!optional) {
        apiError(`Invalid ${method}(...) call! It requires ${minNumParams} argument(s) and it's missing ${name} ${getTypeName(type)}.`)
      }
      return acc && optional && numArgs >= minNumParams
    }

    const arg = args[index]
    let valid = false

    if (optional && isNil(arg)) {
      valid = true
    } else if (type === Function || type === Object || type === String || type === Number || type === Boolean) {
      valid = (typeof arg === 'function') ? type === Function : is(type, arg)
      if (!valid) {
        apiError(`Invalid ${method}(...) call! ${name} (arg ${index}) should be ${getTypeName(type)} instead of ${getTypeName(arg)}.`)
      }
    } else {
      const validate = type as ValidatorFunction
      valid = validate(method, name, optional, arg)
    }

    return acc && valid
  }, true)
}

export const validateMethod = <T>(method: string, args?: T[]): boolean => {
  if (VALIDATOR_CONFIG[method]) {
    return areArgsValid(method, args, VALIDATOR_CONFIG[method])
  }

  const hasArgs = !isNil(args) && is(Array, args) && !!args.length
  if (hasArgs) {
    apiWarning(`${method}() should ideally be called without arguments.`)
  }

  return true
}
